"use client";
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PopOver from "../../atoms/popOver";
import AddonLayout from "./addonLayout";
import { addOnModalVisibility } from "../../../store/slices/common/addOn";
import {
  resetGetDeliveryDateAndGeoPriceAction,
  resetMultiProductDateRange,
  setDeliveryDetailsAction,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import { BUY_NOW, MAKE_IT_EXTRA_SPECIAL } from "../../../constants/common/common";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { pdpAddOnModalVisibility, resetPdpSelectedAddons } from "../../../store/slices/pdpAddonSlice";
import LottieLoader from "../../atoms/lottieLoader";
import { redirectToCheckout } from "../../../utils/common";
import Drawer from "../../molecules/drawer";
import Typography from "../../atoms/typography";

/**
 * Builds the shared add-on modal "close" handler: resets delivery/addon state and,
 * depending on how the flow was entered, redirects to checkout (Buy Now) or clears
 * the cart-initiated addon flag. Shared by `AddonSelection` and any other surface
 * (e.g. the mobile pre-checkout drawer) that renders `AddonLayout` directly.
 *
 * @param {Object} params - Hook params.
 * @param {Object} params.commonCleverTapData - Common CleverTap payload.
 * @returns {Function} onClose handler.
 */
export const useAddonCloseHandler = ({ commonCleverTapData }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { isBuyNowClicked } = useSelector(
    (state) => ({
      isBuyNowClicked: state.pdp?.checkBuyNowOrProceedToPay,
    }),
    shallowEqual,
  );
  const addonViewAndClose = {
    ...commonCleverTapData,
    is_date_today: null,
    is_date_tomorrow: null,
    is_variant_selected: null,
    flow: "new",
  };
  delete addonViewAndClose.delivery_shipping_fee;

  return () => {
    dispatch(addOnModalVisibility(false));
    dispatch(pdpAddOnModalVisibility(false));
    dispatch(setDeliveryDetailsAction({}));
    dispatch(resetGetDeliveryDateAndGeoPriceAction());
    dispatch(resetMultiProductDateRange());
    dispatch(resetPdpSelectedAddons());
    if (isBuyNowClicked === BUY_NOW) {
      redirectToCheckout();
    }
    trackEvent(cleverTapCommonEvents.addonClosed, addonViewAndClose, ["clevertap"]);
  };
};

/**
 * Component that handles the selection of add-ons,
 * allowing users to choose from available options and manage their selections.
 *
 * @returns {JSX.Element} The rendered add-on selection component.
 */
const AddonSelection = ({ isMobile, commonCleverTapData, isShowAddOnBottomSheet, isSuppressed = false }) => {
  const { trackEvent } = useAnalytics();
  const { addOnInfo, pdpAddOnInfo, isAddOnModalOpen, addOnLoading, isPdpModalOpen, pdpLoading } = useSelector(
    (state) => ({
      addOnInfo: state?.addOn?.addOnInfo,
      pdpAddOnInfo: state?.pdpAddon?.addOnInfo,
      isAddOnModalOpen: state.addOn?.isModalOpen,
      addOnLoading: state.addOn?.loading,
      isPdpModalOpen: state.pdpAddon?.isModalOpen,
      pdpLoading: state.pdpAddon?.loading,
    }),
    shallowEqual,
  );
  const onClose = useAddonCloseHandler({ commonCleverTapData });
  // Survives AddonBody remount while the ATC addon popup is open.
  const addonPopupViewedKeyRef = useRef("");

  useEffect(() => {
    if (!(isAddOnModalOpen || isPdpModalOpen)) {
      addonPopupViewedKeyRef.current = "";
    }
  }, [isAddOnModalOpen, isPdpModalOpen]);

  useEffect(() => {
    // When suppressed, this instance never shows its own popup/drawer (e.g. mobile PreCheckoutDrawer
    // owns that surface instead) - so it must not track "not viewed" for a popup it was never going to show.
    if (isSuppressed) {
      return;
    }

    const isAddOnNA = addOnInfo?.isAddonsAvailable === "N";
    const isPdpAddOnNA = pdpAddOnInfo?.isAddonsAvailable === "N";

    if (isAddOnNA || isPdpAddOnNA) {
      const payload = { ...commonCleverTapData };

      // add is_Popup only when PDP addons are not available
      if (isPdpAddOnNA) {
        payload.is_Popup = false;
      }

      delete payload?.attribution_url;
      delete payload?.currency;
      delete payload?.image_url;
      delete payload?.is_variant_selected;
      delete payload?.delivery_shipping_fee;

      trackEvent(cleverTapCommonEvents.addonNotViewed, { ...payload, flow: "new" }, ["clevertap"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOnInfo?.isAddonsAvailable, pdpAddOnInfo?.isAddonsAvailable, isSuppressed]);

  if (isSuppressed) {
    return (
      <>
        {(addOnLoading || pdpLoading) && <LottieLoader />}
        <div id="addon-details" />
      </>
    );
  }

  return (
    <>
      {(addOnLoading || pdpLoading) && <LottieLoader />}
      {isShowAddOnBottomSheet && isMobile ? (
        <Drawer
          position="bottom"
          isOpen={isAddOnModalOpen || isPdpModalOpen}
          onDrawerClose={onClose}
          isPreventUnlockBody={true}
          isCleanUp={false}
          styles="max-h-[93.5%] pt-0 pb-0"
        >
          <Typography variant="p" Tag="p" classes="inter text-16 font-600 leading-4 capitalize text-black-100 m-16">
            {MAKE_IT_EXTRA_SPECIAL}
          </Typography>
          <div className="h-full w-full">
            <AddonLayout
              onClose={onClose}
              commonCleverTapData={commonCleverTapData}
              isBottomDrawer={true}
              isMobile={isMobile}
              addonViewedKeyRef={addonPopupViewedKeyRef}
            />
          </div>
        </Drawer>
      ) : (
        <PopOver
          isVisible={isAddOnModalOpen || isPdpModalOpen}
          classes={
            "md:top-[10%] md:bottom-auto  md:overflow-y-hidden md:left-[9%] w-full md:w-[80%] rounded-none md:rounded-2xl"
          }
          isBackdrop={true}
        >
          <AddonLayout
            onClose={onClose}
            commonCleverTapData={commonCleverTapData}
            isBottomDrawer={false}
            isMobile={isMobile}
            addonViewedKeyRef={addonPopupViewedKeyRef}
          />
        </PopOver>
      )}
      <div id="addon-details" />
    </>
  );
};

export default AddonSelection;

AddonSelection.propTypes = {
  isMobile: PropTypes.bool,
  commonCleverTapData: PropTypes.object,
  isShowAddOnBottomSheet: PropTypes.bool,
  isSuppressed: PropTypes.bool,
};
